import { useEffect, useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Send, Filter } from "lucide-react";
import { toast } from "sonner";

const emptyCampaign = { name: "", message: "", target: "all_customers", custom_phones: "", tier_filter: "" };
const emptySegment = { tier: "", gender: "", birthday_month: "", last_purchase_days_min: "", last_purchase_days_max: "", lifetime_spend_min: "", whatsapp_optin: true };

export default function Campaigns() {
  const { activeBranchId, user } = useAuth();
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [individualOpen, setIndividualOpen] = useState(false);
  const [individualForm, setIndividualForm] = useState({ customer_id: "", message: "" });
  const [form, setForm] = useState(emptyCampaign);
  const [segment, setSegment] = useState(emptySegment);
  const [segPreview, setSegPreview] = useState(null);

  const load = async () => {
    const [c, t, cu] = await Promise.all([
      api.get("/whatsapp/campaigns"),
      api.get("/whatsapp/templates"),
      api.get("/customers"),
    ]);
    setItems(c.data); setTemplates(t.data); setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        branch_id: activeBranchId, name: form.name, message: form.message, target: form.target,
        tier_filter: form.tier_filter || null,
        custom_phones: form.target === "custom_phones"
          ? form.custom_phones.split(",").map((s) => s.trim()).filter(Boolean)
          : (segPreview?.customers || []).map((c) => c.phone),
      };
      // If segment-target chosen with no preview yet, server-side won't filter — fall back to all
      await api.post("/whatsapp/campaigns", body);
      toast.success("Submitted for approval");
      setOpen(false); setForm(emptyCampaign); setSegment(emptySegment); setSegPreview(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const previewSegment = async () => {
    try {
      const body = {
        branch_id: activeBranchId,
        tier: segment.tier || null, gender: segment.gender || null,
        birthday_month: segment.birthday_month ? parseInt(segment.birthday_month) : null,
        last_purchase_days_min: segment.last_purchase_days_min ? parseInt(segment.last_purchase_days_min) : null,
        last_purchase_days_max: segment.last_purchase_days_max ? parseInt(segment.last_purchase_days_max) : null,
        lifetime_spend_min: segment.lifetime_spend_min ? parseFloat(segment.lifetime_spend_min) : null,
        whatsapp_optin: segment.whatsapp_optin,
      };
      const { data } = await api.post("/whatsapp/segment", body);
      setSegPreview(data);
      toast.success(`${data.count} customers • Est. ${formatINR(data.estimated_cost)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendIndividual = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/whatsapp/send-individual", individualForm);
      toast.success(data.sent ? `Sent via ${data.via} • Cost ${formatINR(data.cost)}` : `Queued for approval • Cost ${formatINR(data.cost)}`);
      setIndividualOpen(false); setIndividualForm({ customer_id: "", message: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applyTemplate = (tpl) => setForm((f) => ({ ...f, message: tpl.body, name: f.name || tpl.name }));

  const statusBadge = (s) => {
    const map = { pending_approval: "bg-amber-500", approved: "bg-blue-600", rejected: "bg-red-600", sent: "bg-green-600", draft: "bg-slate-400" };
    return <Badge className={`rounded-none ${map[s] || "bg-slate-400"}`}>{s.replace("_", " ")}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex justify-end gap-2">
        <Dialog open={individualOpen} onOpenChange={setIndividualOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-none" data-testid="individual-msg-btn">
              <Send className="w-4 h-4 mr-2" />Direct Message
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle>Send WhatsApp to Individual Customer</DialogTitle></DialogHeader>
            <form onSubmit={sendIndividual} className="space-y-3">
              <div><Label>Customer</Label>
                <select required value={individualForm.customer_id} onChange={(e) => setIndividualForm({ ...individualForm, customer_id: e.target.value })} className="w-full border border-slate-200 rounded-none p-2 mt-1 text-sm" data-testid="ind-customer">
                  <option value="">Select…</option>
                  {customers.filter((c) => c.whatsapp_optin !== false).map((c) => (
                    <option key={c.id} value={c.id}>{c.name} • {c.phone} • {c.tier || "silver"}</option>
                  ))}
                </select>
              </div>
              <div><Label>Message</Label>
                <textarea required rows={4} value={individualForm.message} onChange={(e) => setIndividualForm({ ...individualForm, message: e.target.value })} className="w-full border border-slate-200 rounded-none p-2 mt-1 text-sm" data-testid="ind-message" />
                <p className="text-[10px] text-slate-500 mt-1">Variables: {"{customer_name}"} {"{tier}"} {"{points}"}</p>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 p-2">
                {user.role === "super_admin" ? "Will send immediately." : "Will queue for Super Admin approval."}
              </div>
              <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="ind-send">Send</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyCampaign); setSegment(emptySegment); setSegPreview(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="new-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>WhatsApp Campaign</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Campaign Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="camp-name" /></div>

              {templates.length > 0 && (
                <div>
                  <Label>Quick-load template</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {templates.map((t) => (
                      <button key={t.id} type="button" onClick={() => applyTemplate(t)} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 border border-slate-200" data-testid={`camp-tpl-${t.id}`}>{t.name}</button>
                    ))}
                  </div>
                </div>
              )}

              <div><Label>Message</Label><textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-slate-200 rounded-none p-2 mt-1 text-sm" data-testid="camp-message" /></div>

              <Tabs value={form.target === "custom_phones" ? "custom" : "segment"} onValueChange={(t) => setForm({ ...form, target: t === "custom" ? "custom_phones" : "all_customers" })} data-testid="camp-target-tabs">
                <TabsList className="rounded-none">
                  <TabsTrigger value="segment" data-testid="camp-tab-segment"><Filter className="w-3 h-3 mr-1" />Segment Builder</TabsTrigger>
                  <TabsTrigger value="custom" data-testid="camp-tab-custom">Custom Phones</TabsTrigger>
                </TabsList>
                <TabsContent value="segment" className="space-y-3 pt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Tier</Label>
                      <select value={segment.tier} onChange={(e) => setSegment({ ...segment, tier: e.target.value })} className="w-full border border-slate-200 rounded-none p-1.5 mt-1 text-xs" data-testid="seg-tier">
                        <option value="">Any</option><option value="silver">Silver</option><option value="gold">Gold</option><option value="platinum">Platinum</option>
                      </select>
                    </div>
                    <div><Label className="text-xs">Gender</Label>
                      <select value={segment.gender} onChange={(e) => setSegment({ ...segment, gender: e.target.value })} className="w-full border border-slate-200 rounded-none p-1.5 mt-1 text-xs" data-testid="seg-gender">
                        <option value="">Any</option><option value="female">Female</option><option value="male">Male</option>
                      </select>
                    </div>
                    <div><Label className="text-xs">Birthday Month (1-12)</Label><Input type="number" min="1" max="12" value={segment.birthday_month} onChange={(e) => setSegment({ ...segment, birthday_month: e.target.value })} className="rounded-none h-8 text-xs mt-1" data-testid="seg-birthday" /></div>
                    <div><Label className="text-xs">Lifetime Spend ≥ ₹</Label><Input type="number" value={segment.lifetime_spend_min} onChange={(e) => setSegment({ ...segment, lifetime_spend_min: e.target.value })} className="rounded-none h-8 text-xs mt-1" data-testid="seg-spend" /></div>
                    <div><Label className="text-xs">Idle for ≥ days</Label><Input type="number" value={segment.last_purchase_days_min} onChange={(e) => setSegment({ ...segment, last_purchase_days_min: e.target.value })} className="rounded-none h-8 text-xs mt-1" data-testid="seg-idle-min" /></div>
                    <div><Label className="text-xs">Idle for ≤ days</Label><Input type="number" value={segment.last_purchase_days_max} onChange={(e) => setSegment({ ...segment, last_purchase_days_max: e.target.value })} className="rounded-none h-8 text-xs mt-1" data-testid="seg-idle-max" /></div>
                  </div>
                  <Button type="button" onClick={previewSegment} variant="outline" className="rounded-none" data-testid="seg-preview-btn"><Filter className="w-4 h-4 mr-1" />Preview Segment</Button>
                  {segPreview && (
                    <div className="bg-blue-50 border border-blue-200 p-3 text-sm" data-testid="seg-preview-result">
                      <div className="font-semibold">{segPreview.count} customers match</div>
                      <div className="text-xs text-slate-600">Nearby estimate: {formatINR(segPreview.estimated_cost)}</div>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="custom" className="pt-3">
                  <Label>Phone numbers (comma-separated, E.164)</Label>
                  <Input value={form.custom_phones} onChange={(e) => setForm({ ...form, custom_phones: e.target.value })} className="rounded-none mt-1" placeholder="+919900001111, +919900002222" data-testid="camp-custom-phones" />
                </TabsContent>
              </Tabs>

              <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 p-2">This campaign will enter the Super Admin approval queue before sending.</div>
              <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="camp-submit">Submit for Approval</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Name</th>
              <th className="px-4 py-3 text-left font-semibold">Kind</th>
              <th className="px-4 py-3 text-right font-semibold">Recipients</th>
              <th className="px-4 py-3 text-right font-semibold">Nearby Estimate</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Requested</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No campaigns yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`campaign-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-slate-600 text-xs capitalize">{c.kind || c.target.replace("_", " ")}</td>
                <td className="px-4 py-3 text-right tabular-nums">{c.estimated_recipients}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatINR(c.estimated_cost)}</td>
                <td className="px-4 py-3">{statusBadge(c.status)}</td>
                <td className="px-4 py-3 text-slate-600">{c.requested_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
